/*
  Scroll-linked parallax for the "Testimonials stagger" section.
  Each card carries data-parallax-start / data-parallax-stop (percent of
  the card's own height). As the card travels through the viewport it
  drifts from start to stop:

    t = clamp01((viewportHeight - cardTopInViewport) / (viewportHeight + cardHeight))
    translateY_px = -(start + (stop - start) * t) / 100 * cardHeight

  Disabled below 750px and under prefers-reduced-motion: reduce (cards
  stay in their natural flow positions, no transform at all).
*/
class TestimonialParallax extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-parallax-start]'));
    if (this.cards.length === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktop = window.matchMedia('(min-width: 750px)');
    // Last translateY applied per card, so layout reads can recover the
    // card's natural (untransformed) viewport position.
    this.applied = new Map();
    this.framePending = false;
    this.visible = false;

    this.update = this.update.bind(this);
    this.requestUpdate = this.requestUpdate.bind(this);
    this.onSettingsChange = this.onSettingsChange.bind(this);

    this.intersectionObserver = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) {
        this.requestUpdate();
      }
    });
    this.intersectionObserver.observe(this);

    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.requestUpdate);
    this.reducedMotion.addEventListener('change', this.onSettingsChange);
    this.desktop.addEventListener('change', this.onSettingsChange);
  }

  disconnectedCallback() {
    this.intersectionObserver.disconnect();
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.requestUpdate);
    this.reducedMotion.removeEventListener('change', this.onSettingsChange);
    this.desktop.removeEventListener('change', this.onSettingsChange);
  }

  onSettingsChange() {
    if (this.reducedMotion.matches || !this.desktop.matches) {
      this.clearTransforms();
    }
    this.requestUpdate();
  }

  clearTransforms() {
    for (const card of this.cards) {
      card.style.transform = '';
      this.applied.delete(card);
    }
  }

  requestUpdate() {
    if (!this.visible || this.framePending) return;
    if (this.reducedMotion.matches || !this.desktop.matches) return;
    this.framePending = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.framePending = false;
    if (!this.visible || this.reducedMotion.matches || !this.desktop.matches) return;

    const viewportHeight = window.innerHeight;

    // Read all layout first, then write transforms (no interleaved
    // read/write thrash).
    const pending = [];
    for (const card of this.cards) {
      const start = parseFloat(card.dataset.parallaxStart) || 0;
      const stop = parseFloat(card.dataset.parallaxStop) || 0;
      const current = this.applied.get(card) || 0;
      const rect = card.getBoundingClientRect();
      const naturalTop = rect.top - current;
      const cardHeight = rect.height;
      if (cardHeight === 0) continue;

      let t = (viewportHeight - naturalTop) / (viewportHeight + cardHeight);
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const translateY = (-(start + (stop - start) * t) / 100) * cardHeight;

      if (Math.abs(translateY - current) > 0.1) {
        pending.push([card, translateY]);
      }
    }

    for (const [card, translateY] of pending) {
      card.style.transform = `translateY(${translateY.toFixed(2)}px)`;
      this.applied.set(card, translateY);
    }
  }
}

customElements.define('testimonial-parallax', TestimonialParallax);
